import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Store, MapPin, Users, Star, Filter, ArrowUpRight, Clock, UserCheck, AlertTriangle } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ComposedChart, Line, Bar, Cell } from 'recharts';
// Level options
const levels = [{
  id: 'konzern',
  name: 'Konzern-Übersicht',
  icon: Building2
}, {
  id: 'region',
  name: 'Regionen',
  icon: MapPin
}, {
  id: 'markt',
  name: 'Märkte',
  icon: Store
}];
// Market data with staff metrics
const marketsData = [{
  id: 'hamburg-hafencity',
  name: 'Lidl XL Hamburg-Hafencity',
  region: 'Deutschland',
  rating: 4.3,
  staff: 45,
  size: 2800,
  staffPerSqm: 16.1
}, {
  id: 'berlin-kreuzberg',
  name: 'Lidl Berlin-Kreuzberg',
  region: 'Deutschland',
  rating: 3.8,
  staff: 18,
  size: 1200,
  staffPerSqm: 15.0
}, {
  id: 'muenchen-schwabing',
  name: 'Lidl XL München-Schwabing',
  region: 'Deutschland',
  rating: 4.6,
  staff: 32,
  size: 1800,
  staffPerSqm: 17.8
}, {
  id: 'koeln-ehrenfeld',
  name: 'Lidl Express Köln-Ehrenfeld',
  region: 'Deutschland',
  rating: 3.5,
  staff: 14,
  size: 1100,
  staffPerSqm: 12.7
}, {
  id: 'frankfurt-sachsenhausen',
  name: 'Lidl XL Frankfurt-Sachsenhausen',
  region: 'Deutschland',
  rating: 4.4,
  staff: 28,
  size: 1600,
  staffPerSqm: 17.5
}, {
  id: 'dortmund-innenstadt',
  name: 'Lidl Dortmund-Innenstadt',
  region: 'Deutschland',
  rating: 3.9,
  staff: 20,
  size: 1400,
  staffPerSqm: 14.3
}, {
  id: 'leipzig-sued',
  name: 'Lidl XL Leipzig-Süd',
  region: 'Deutschland',
  rating: 4.5,
  staff: 38,
  size: 2200,
  staffPerSqm: 17.3
}, {
  id: 'stuttgart-west',
  name: 'Lidl Stuttgart-West',
  region: 'Deutschland',
  rating: 4.2,
  staff: 24,
  size: 1500,
  staffPerSqm: 16.0
}, {
  id: 'freiburg-zentrum',
  name: 'Lidl XL Freiburg',
  region: 'Deutschland',
  rating: 4.1,
  staff: 21,
  size: 1350,
  staffPerSqm: 15.6
}, {
  id: 'kiel-zentrum',
  name: 'Lidl XL Kiel',
  region: 'Deutschland',
  rating: 4.4,
  staff: 27,
  size: 1650,
  staffPerSqm: 16.4
}, {
  id: 'hannover-list',
  name: 'Lidl XL Hannover-List',
  region: 'Deutschland',
  rating: 4.0,
  staff: 19,
  size: 1300,
  staffPerSqm: 14.6
}, {
  id: 'nuernberg-zentrum',
  name: 'Lidl XL Nürnberg-Zentrum',
  region: 'Deutschland',
  rating: 4.3,
  staff: 28,
  size: 1700,
  staffPerSqm: 16.5
}, {
  id: 'dresden-altstadt',
  name: 'Lidl XL Dresden-Altstadt',
  region: 'Deutschland',
  rating: 4.1,
  staff: 19,
  size: 1250,
  staffPerSqm: 15.2
}, {
  id: 'duesseldorf-bilk',
  name: 'Lidl XL Düsseldorf-Bilk',
  region: 'Deutschland',
  rating: 4.2,
  staff: 25,
  size: 1550,
  staffPerSqm: 16.1
}, {
  id: 'bremen-mitte',
  name: 'Lidl Express Bremen-Mitte',
  region: 'Deutschland',
  rating: 3.9,
  staff: 16,
  size: 1150,
  staffPerSqm: 13.9
}];
// Region aggregated data -> Countries
const regionsData = [{
  id: 'deutschland',
  name: 'Deutschland',
  rating: 4.2,
  staff: 88,
  size: 5600,
  staffPerSqm: 15.7,
  markets: 3
}, {
  id: 'frankreich',
  name: 'Frankreich',
  rating: 3.9,
  staff: 37,
  size: 2500,
  staffPerSqm: 14.8,
  markets: 2
}, {
  id: 'spanien',
  name: 'Spanien',
  rating: 3.87,
  staff: 59,
  size: 4050,
  staffPerSqm: 14.6,
  markets: 3
}, {
  id: 'italien',
  name: 'Italien',
  rating: 4.4,
  staff: 28,
  size: 1600,
  staffPerSqm: 17.5,
  markets: 1
}, {
  id: 'uk',
  name: 'Großbritannien',
  rating: 4.3,
  staff: 85,
  size: 5150,
  staffPerSqm: 16.5,
  markets: 3
}, {
  id: 'polen',
  name: 'Polen',
  rating: 4.15,
  staff: 45,
  size: 2850,
  staffPerSqm: 15.8,
  markets: 2
}, {
  id: 'niederlande',
  name: 'Niederlande',
  rating: 4.6,
  staff: 32,
  size: 1800,
  staffPerSqm: 17.8,
  markets: 1
}];
// Konzern data
const konzernData = {
  id: 'konzern',
  name: 'Lidl Gesamtkonzern',
  rating: 4.15,
  staff: 374,
  size: 23550,
  staffPerSqm: 15.9,
  markets: 15,
  regions: 7
};
// Time series data
const timeSeriesData = [{
  month: 'Feb 23',
  rating: 4.05,
  staffPerSqm: 14.8
}, {
  month: 'Mär 23',
  rating: 4.08,
  staffPerSqm: 15.0
}, {
  month: 'Apr 23',
  rating: 4.06,
  staffPerSqm: 14.9
}, {
  month: 'Mai 23',
  rating: 4.1,
  staffPerSqm: 15.2
}, {
  month: 'Jun 23',
  rating: 4.12,
  staffPerSqm: 15.4
}, {
  month: 'Jul 23',
  rating: 4.09,
  staffPerSqm: 15.3
}, {
  month: 'Aug 23',
  rating: 4.11,
  staffPerSqm: 15.5
}, {
  month: 'Sep 23',
  rating: 4.14,
  staffPerSqm: 15.6
}, {
  month: 'Okt 23',
  rating: 4.13,
  staffPerSqm: 15.7
}, {
  month: 'Nov 23',
  rating: 4.16,
  staffPerSqm: 15.8
}, {
  month: 'Dez 23',
  rating: 4.18,
  staffPerSqm: 15.9
}, {
  month: 'Jan 24',
  rating: 4.2,
  staffPerSqm: 15.9
}];
// Calculate correlation coefficient
const calculateCorrelation = (data: Array<{
  rating: number;
  staffPerSqm: number;
}>) => {
  const n = data.length;
  const sumX = data.reduce((acc, d) => acc + d.staffPerSqm, 0);
  const sumY = data.reduce((acc, d) => acc + d.rating, 0);
  const sumXY = data.reduce((acc, d) => acc + d.staffPerSqm * d.rating, 0);
  const sumX2 = data.reduce((acc, d) => acc + d.staffPerSqm * d.staffPerSqm, 0);
  const sumY2 = data.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  return denominator === 0 ? 0 : numerator / denominator;
};
// Custom tooltip for scatter chart
const CustomScatterTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm" data-id="element-1050">
        <div className="font-semibold text-gray-900 mb-2" data-id="element-1051">{data.name}</div>
        <div className="space-y-1" data-id="element-1052">
          <div className="flex justify-between gap-4" data-id="element-1053">
            <span className="text-gray-500" data-id="element-1054">Rating:</span>
            <span className="font-medium" data-id="element-1055">{data.rating.toFixed(1)} ★</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1056">
            <span className="text-gray-500" data-id="element-1057">Personal/1000m²:</span>
            <span className="font-medium" data-id="element-1058">{data.staffPerSqm.toFixed(1)}</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1059">
            <span className="text-gray-500" data-id="element-1060">Mitarbeiter:</span>
            <span className="font-medium" data-id="element-1061">{data.staff}</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1062">
            <span className="text-gray-500" data-id="element-1063">Fläche:</span>
            <span className="font-medium" data-id="element-1064">
              {data.size.toLocaleString('de-DE')} m²
            </span>
          </div>
        </div>
      </div>;
  }
  return null;
};
export function PersonalReputationPage() {
  const [selectedLevel, setSelectedLevel] = useState<'konzern' | 'region' | 'markt'>('markt');
  // Get data based on selected level
  const getScatterData = () => {
    switch (selectedLevel) {
      case 'markt':
        return marketsData.map(m => ({
          ...m,
          z: m.size
        }));
      case 'region':
        return regionsData.map(r => ({
          ...r,
          z: r.size
        }));
      case 'konzern':
        return [{
          ...konzernData,
          z: konzernData.size
        }];
    }
  };
  const scatterData = getScatterData();
  const correlation = calculateCorrelation(scatterData);
  // Calculate averages
  const avgStaffPerSqm = scatterData.reduce((acc, d) => acc + d.staffPerSqm, 0) / scatterData.length;
  const avgRating = scatterData.reduce((acc, d) => acc + d.rating, 0) / scatterData.length;
  // Find understaffed markets (low staff, low rating)
  const understaffed = [...scatterData].filter(d => d.staffPerSqm < avgStaffPerSqm && d.rating < avgRating).sort((a, b) => a.staffPerSqm - b.staffPerSqm).slice(0, 3);
  // Find well-staffed high performers (high staff, high rating)
  const topPerformers = [...scatterData].filter(d => d.staffPerSqm >= avgStaffPerSqm).sort((a, b) => b.rating - a.rating).slice(0, 3);
  // Calculate potential improvement
  const potentialImprovement = understaffed.reduce((acc, d) => {
    const staffGap = avgStaffPerSqm - d.staffPerSqm;
    const estimatedRatingGain = staffGap * 0.08;
    return acc + estimatedRatingGain;
  }, 0) / understaffed.length || 0;
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-1065">
      {/* Level Filter */}
      <Card className="!p-4" data-id="element-1066">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-1067">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1068">
            <Filter size={16} data-id="element-1069" />
            <span data-id="element-1070">Ebene:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1071">
            {levels.map(level => {
            const isSelected = selectedLevel === level.id;
            const Icon = level.icon;
            return <button key={level.id} onClick={() => setSelectedLevel(level.id as any)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-lidl-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1072">
                  <Icon size={14} data-id="element-1073" />
                  {level.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1074">
        <KPICard title="Korrelation Personal ↔ Rating" value={correlation.toFixed(2)} change={correlation > 0.6 ? 'Stark positiv' : correlation > 0.3 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-1076" />} subtext="Pearson-Koeffizient" data-id="element-1075" />
        <KPICard title="Ø Personal pro 1000m²" value={avgStaffPerSqm.toFixed(1)} change="+1.2 vs. Vorjahr" trend="up" icon={<Users size={18} data-id="element-1078" />} subtext="Mitarbeiter" data-id="element-1077" />
        <KPICard title="Ø Bewertung" value={avgRating.toFixed(2)} change="+0.15" trend="up" icon={<Star size={18} data-id="element-1080" />} subtext="Google Rating" data-id="element-1079" />
        <KPICard title="Verbesserungspotenzial" value={`+${potentialImprovement.toFixed(2)}★`} change={`${understaffed.length} Märkte`} trend="up" icon={<UserCheck size={18} data-id="element-1082" />} subtext="bei Ø-Besetzung" data-id="element-1081" />
      </div>

      {/* Main Scatter Chart */}
      <Card data-id="element-1083">
        <div className="p-6" data-id="element-1084">
          <div className="flex items-center justify-between mb-6" data-id="element-1085">
            <div data-id="element-1086">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1087">
                Personalbesetzung vs. Kundenbewertung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1088">
                Blasengröße = Verkaufsfläche (m²) • Personal pro 1000m²
                Verkaufsfläche
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-1089">
              <div className="flex items-center gap-1" data-id="element-1090">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#639754'
              }} data-id="element-1091"></div>
                <span data-id="element-1092">Gut besetzt</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1093">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#DF0000'
              }} data-id="element-1094"></div>
                <span data-id="element-1095">Unterbesetzt</span>
              </div>
            </div>
          </div>

          <div className="h-96" data-id="element-1096">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1097">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-1098">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1099" />
                <XAxis type="number" dataKey="staffPerSqm" name="Personal/1000m²" domain={[12, 19]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Personal pro 1000m² Verkaufsfläche',
                position: 'bottom',
                offset: 0,
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1100" />
                <YAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Google Rating (Sterne)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1101" />
                <ZAxis type="number" dataKey="z" range={[100, 800]} data-id="element-1102" />
                <Tooltip content={<CustomScatterTooltip data-id="element-1104" />} data-id="element-1103" />
                <Scatter name="Standorte" data={scatterData} fill="#0050AA" data-id="element-1105">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.staffPerSqm >= avgStaffPerSqm ? '#639754' : entry.staffPerSqm >= avgStaffPerSqm - 1.5 ? '#FFF000' : '#DF0000'} fillOpacity={0.7} data-id="element-1106" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* Trend line info */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm" data-id="element-1107">
            <div className="flex items-center gap-2" data-id="element-1108">
              <TrendingUp size={16} style={{
              color: '#639754'
            }} data-id="element-1109" />
              <span className="text-gray-600" data-id="element-1110">
                Korrelation:{' '}
                <span className="font-semibold" style={{
                color: correlation > 0.5 ? '#639754' : '#FFF000'
              }} data-id="element-1111">
                  {(correlation * 100).toFixed(0)}%
                </span>{' '}
                – Mehr Personal korreliert mit besseren Bewertungen
              </span>
            </div>
            <div className="text-gray-500" data-id="element-1112">
              Ø Besetzung: {avgStaffPerSqm.toFixed(1)} MA/1000m²
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1113">
        {/* Top Performers */}
        <Card data-id="element-1114">
          <div className="p-6" data-id="element-1115">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1116">
              <ArrowUpRight className="text-green-600" size={20} data-id="element-1117" />
              Top Performer: Gut besetzt & hohe Bewertung
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1118">
              Märkte mit überdurchschnittlicher Besetzung und Rating
            </p>

            <div className="space-y-3" data-id="element-1119">
              {topPerformers.length > 0 ? topPerformers.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-1120">
                    <div className="flex items-center gap-3" data-id="element-1121">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#639754'
                }} data-id="element-1122">
                        {index + 1}
                      </div>
                      <div data-id="element-1123">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-1124">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1125">
                          {item.staff} MA • {item.size.toLocaleString('de-DE')}{' '}
                          m²
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1126">
                      <div className="font-bold text-lg" style={{
                  color: '#639754'
                }} data-id="element-1127">
                        {item.rating.toFixed(1)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1128">
                        {item.staffPerSqm.toFixed(1)} MA/1000m²
                      </div>
                    </div>
                  </motion.div>) : <div className="text-center py-8 text-gray-500" data-id="element-1129">
                  Keine Daten verfügbar
                </div>}
            </div>

            {topPerformers.length > 0 && <div className="mt-4 p-3 bg-green-50 rounded-lg" data-id="element-1130">
                <div className="text-sm text-green-800" data-id="element-1131">
                  <strong data-id="element-1132">Erkenntnis:</strong> Diese Märkte zeigen, dass eine
                  Besetzung von{' '}
                  <strong data-id="element-1133">
                    {(topPerformers.reduce((acc, d) => acc + d.staffPerSqm, 0) / topPerformers.length).toFixed(1)}{' '}
                    MA/1000m²
                  </strong>{' '}
                  mit Ratings von{' '}
                  <strong data-id="element-1134">
                    {(topPerformers.reduce((acc, d) => acc + d.rating, 0) / topPerformers.length).toFixed(1)}
                    + Sternen
                  </strong>{' '}
                  einhergeht.
                </div>
              </div>}
          </div>
        </Card>

        {/* Understaffed Markets */}
        <Card data-id="element-1135">
          <div className="p-6" data-id="element-1136">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1137">
              <AlertTriangle className="text-red-600" size={20} data-id="element-1138" />
              Handlungsbedarf: Unterbesetzte Märkte
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1139">
              Märkte mit unterdurchschnittlicher Besetzung und Rating
            </p>

            <div className="space-y-3" data-id="element-1140">
              {understaffed.length > 0 ? understaffed.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fde8e8'
            }} data-id="element-1141">
                    <div className="flex items-center gap-3" data-id="element-1142">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#DF0000'
                }} data-id="element-1143">
                        !
                      </div>
                      <div data-id="element-1144">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-1145">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1146">
                          {item.staff} MA • {item.size.toLocaleString('de-DE')}{' '}
                          m²
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1147">
                      <div className="font-bold text-lg" style={{
                  color: '#DF0000'
                }} data-id="element-1148">
                        {item.rating.toFixed(1)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1149">
                        {item.staffPerSqm.toFixed(1)} MA/1000m² (
                        {((avgStaffPerSqm - item.staffPerSqm) / avgStaffPerSqm * 100).toFixed(0)}
                        % unter Ø)
                      </div>
                    </div>
                  </motion.div>) : <div className="text-center py-8 text-gray-500" data-id="element-1150">
                  Keine unterbesetzten Märkte gefunden
                </div>}
            </div>

            {understaffed.length > 0 && <div className="mt-4 p-3 bg-blue-50 rounded-lg" data-id="element-1151">
                <div className="text-sm text-blue-800" data-id="element-1152">
                  <strong data-id="element-1153">Empfehlung:</strong> Bei Erhöhung auf Ø-Besetzung (
                  {avgStaffPerSqm.toFixed(1)} MA/1000m²) ist eine
                  Rating-Verbesserung von ca.{' '}
                  <strong data-id="element-1154">+{potentialImprovement.toFixed(2)} Sternen</strong> zu
                  erwarten.
                </div>
              </div>}
          </div>
        </Card>
      </div>

      {/* Time Series Chart */}
      <Card data-id="element-1155">
        <div className="p-6" data-id="element-1156">
          <div className="flex items-center justify-between mb-6" data-id="element-1157">
            <div data-id="element-1158">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1159">
                Entwicklung über Zeit
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1160">
                Personalbesetzung und Kundenbewertung (12 Monate)
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1161">
              <div className="flex items-center gap-2" data-id="element-1162">
                <div className="w-3 h-3 rounded-full bg-lidl-blue" data-id="element-1163"></div>
                <span className="text-gray-600" data-id="element-1164">Personal/1000m²</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1165">
                <div className="w-6 h-0.5 bg-lidl-yellow" data-id="element-1166"></div>
                <span className="text-gray-600" data-id="element-1167">Rating</span>
              </div>
            </div>
          </div>

          <div className="h-72" data-id="element-1168">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1169">
              <ComposedChart data={timeSeriesData} data-id="element-1170">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-1171" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-1172" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[14, 17]} label={{
                value: 'Personal/1000m²',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1173" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[3.9, 4.4]} label={{
                value: 'Rating',
                angle: 90,
                position: 'insideRight',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1174" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [name === 'staffPerSqm' ? `${value.toFixed(1)} MA/1000m²` : value.toFixed(2), name === 'staffPerSqm' ? 'Personal' : 'Rating']} data-id="element-1175" />
                <Bar yAxisId="left" dataKey="staffPerSqm" fill="#0050AA" radius={[4, 4, 0, 0]} barSize={30} data-id="element-1176" />
                <Line yAxisId="right" type="monotone" dataKey="rating" stroke="#FFF000" strokeWidth={3} dot={{
                fill: '#FFF000',
                strokeWidth: 2,
                r: 4
              }} data-id="element-1177" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Summary */}
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-center" data-id="element-1178">
            <div data-id="element-1179">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1180">
                Personalzuwachs (12 Monate)
              </div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1181">
                +7.4%
              </div>
            </div>
            <div data-id="element-1182">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1183">
                Rating-Verbesserung (12 Monate)
              </div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1184">
                +0.15★
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Data Table */}
      <Card data-id="element-1185">
        <div className="p-6" data-id="element-1186">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1187">
            Detailübersicht{' '}
            {selectedLevel === 'markt' ? 'Märkte' : selectedLevel === 'region' ? 'Regionen' : 'Konzern'}
          </h3>

          <div className="overflow-x-auto" data-id="element-1188">
            <table className="w-full text-sm" data-id="element-1189">
              <thead data-id="element-1190">
                <tr className="border-b border-gray-200" data-id="element-1191">
                  <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1192">
                    Name
                  </th>
                  {selectedLevel === 'markt' && <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1193">
                      Region
                    </th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1194">
                    Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1195">
                    Personal
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1196">
                    Fläche
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1197">
                    MA/1000m²
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1198">
                    vs. Ø
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1199">
                {scatterData.map((item: any, index) => {
                const staffDiff = (item.staffPerSqm - avgStaffPerSqm) / avgStaffPerSqm * 100;
                return <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1200">
                      <td className="py-3 px-4 font-medium text-gray-900" data-id="element-1201">
                        {item.name}
                      </td>
                      {selectedLevel === 'markt' && <td className="py-3 px-4 text-gray-500" data-id="element-1202">
                          {item.region}
                        </td>}
                      <td className="py-3 px-4 text-right" data-id="element-1203">
                        <span className={`font-medium ${item.rating >= 4.3 ? 'text-green-600' : item.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-1204">
                          {item.rating.toFixed(1)} ★
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900" data-id="element-1205">
                        {item.staff} MA
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500" data-id="element-1206">
                        {item.size.toLocaleString('de-DE')} m²
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1207">
                        <span className={`font-medium ${staffDiff >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1208">
                          {item.staffPerSqm.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1209">
                        <span className={`font-medium ${staffDiff >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1210">
                          {staffDiff >= 0 ? '+' : ''}
                          {staffDiff.toFixed(1)}%
                        </span>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}